/**
 * @fileoverview User Management dialogs hook.
 * @description Which user, if any, each User Management dialog (View, Edit, Deactivate,
 * Reactivate) is open for, and how they hand over to one another: the View dialog's Edit,
 * Deactivate and Reactivate actions close it and open that dialog for the same user. The View
 * dialog can start open, for a deep link.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'

/**
 * @description Keeps the id each dialog is open for ('' when closed).
 * @param {string} [initialViewingUserId] - The user the View dialog starts open for, from a deep
 * link, or '' to start closed.
 * @returns {object} Each dialog's user id, and the handlers to open, close and hand over.
 */
export function useUserDialogs(initialViewingUserId = '') {
  const [viewingUserId, setViewingUserId] = useState(initialViewingUserId)
  const [editingUserId, setEditingUserId] = useState('')
  const [deactivatingUserId, setDeactivatingUserId] = useState('')
  const [reactivatingUserId, setReactivatingUserId] = useState('')

  /**
   * @description Closes the View dialog.
   */
  const closeView = () => {
    setViewingUserId('')
  }

  /**
   * @description Closes the Edit dialog.
   */
  const closeEdit = () => {
    setEditingUserId('')
  }

  /**
   * @description Closes the Deactivate dialog.
   */
  const closeDeactivate = () => {
    setDeactivatingUserId('')
  }

  /**
   * @description Closes the Reactivate dialog.
   */
  const closeReactivate = () => {
    setReactivatingUserId('')
  }

  /**
   * @description Hands the user being viewed over to the Edit dialog.
   */
  const editViewedUser = () => {
    setEditingUserId(viewingUserId)
    setViewingUserId('')
  }

  /**
   * @description Hands the user being viewed over to the Deactivate dialog.
   */
  const deactivateViewedUser = () => {
    setDeactivatingUserId(viewingUserId)
    setViewingUserId('')
  }

  /**
   * @description Hands the user being viewed over to the Reactivate dialog.
   */
  const reactivateViewedUser = () => {
    setReactivatingUserId(viewingUserId)
    setViewingUserId('')
  }

  return {
    viewingUserId,
    editingUserId,
    deactivatingUserId,
    reactivatingUserId,
    openView: setViewingUserId,
    openEdit: setEditingUserId,
    openDeactivate: setDeactivatingUserId,
    openReactivate: setReactivatingUserId,
    closeView,
    closeEdit,
    closeDeactivate,
    closeReactivate,
    editViewedUser,
    deactivateViewedUser,
    reactivateViewedUser,
  }
}
